import _ from 'lodash';
import autobind from 'autobind-decorator';
import { observable, action, computed, has, extendObservable } from 'mobx';
import owaspPwordTest from 'owasp-password-strength-test';

import { regex } from 'client/shared/util';

import formConfig from './form-config';

owaspPwordTest.config({
  minLength: 8
});

const validatorsFunctions = {
	email: (value) => regex.email.test(value),
	phone: (value = '') => regex.phone.test(value) || regex.internationalPhone.test(value.replace(/\s/g, '')),
	date: (value) => regex.date.test(value),
	isOfAge: (value => {
		let enteredDOB = new Date(value);
		let currentYear = new Date().getFullYear();
		if(enteredDOB.getFullYear() + 13 > currentYear) {
			return false;
		}
		return true;
	}),

	password: (value, key, store) => {
		const testValue = _.isObject(value) ? value.confirmPassword : value;
		const { errors = [] } = owaspPwordTest.test(testValue || '');

		store.setErrorMessage(key, errors.join(' '));
		return errors.length === 0;
	},

	confirmPassword: ({ password, confirmPassword }) => {
		return _.isString(password) && _.isString(confirmPassword) && password === confirmPassword;
	}
};

const hasValue = (value) => _.isEmpty(value) !== true || _.isNumber(value) || _.isBoolean(value);

class FormStore {

	@observable
	formConfig = formConfig;

	@observable
	_validationErrorMsgs = null;

	@observable
	_shouldStoreFormData = false;

	@computed
	get shouldStoreFormData () {
		return this._shouldStoreFormData;
	}

	set shouldStoreFormData (shouldStoreFormData) {
		this._shouldStoreFormData = {
			...this._shouldStoreFormData,
			...shouldStoreFormData
		};
	}

	@observable
	_validation = {};

	@computed
	get validation () {
		return this._validation;
	}

	set validation (validation) {
		this._validation = {
			...this._validation,
			...validation
		};
	}

	@computed
	get validationErrorMsgs () {
		return this._validationErrorMsgs;
	}

	@action
	setErrorMessage (key, message) {
		if (_.isEmpty(this._validationErrorMsgs)) {

			this._validationErrorMsgs = {};
		}

		this._validationErrorMsgs[key] = message;
	}

	get (key) {
		const fieldConfig = _.get(this.formConfig, key);
		return fieldConfig;
	}

	getErrorMessage (key) {
		if (_.isEmpty(this._validationErrorMsgs)) {

			return null;
		}

		return this._validationErrorMsgs[key];
	}

	@computed
	get config () {
		return this.formConfig;
	}

	@action
	addToConfig (newKey, newConfig) {
		if (_.has(this.formConfig, newKey)) {

			console.warn('Can not overwright an existing form config property.');
			return;
		}

		const newConfigObj = {};
		newConfigObj[newKey] = newConfig;
		extendObservable(this.formConfig, newConfigObj);
	}

	makeReplacement (value, options = {}) {
		if (_.isEmpty(value)) {

			return value;
		}

		const newValue = value.replace(/\{([^\}]+)\}/, (match, capture) => {
			const replacement = Object.assign({}, this.formConfig.replacements[capture], options);
			let replaceValue = this[replacement.key] || replacement.default;

			if (_.isObject(replacement) && replacement.isPossessive) {

				replaceValue = (/s$/i).test(replaceValue) ? `${replaceValue}'` : `${replaceValue}'s`;
			}

			return replaceValue;
		});

		return newValue || value;
	}

	checkConditions (conditions) {
		if (_.isEmpty(conditions) || _.isEmpty(conditions.list)) {

			return true;
		}

		let result = false;

		const testCondition = (condition) => {
			const { value, field } = condition;
			const actualValue = this.get(field);

			if (_.isFunction(value)) {

				return value(actualValue);
			}

			return value.indexOf(actualValue) >= 0;
		}

		_.each(conditions.list, (condition) => {

			if (testCondition(condition)) {

				result = true;
			} else if ((/and/i).test(conditions.operator)) {

				// Stops execution of the iterator function
				return result = false;
			}
		});

		return result;
	}

	getInjectOptions ({ injectOptions, dataStore }) {
		const newOptions = [];

		if (_.isEmpty(injectOptions)) {

			return newOptions;
		}

		_.each(injectOptions.options, ({ collection, listKey, filter, labelProp, valueProp }) => {
			let list = _.isString(listKey) ? dataStore[listKey] : dataStore.items;

			if (_.isFunction(filter)) {

				list = _.filter(list, filter);
			}

			_.each(list, (item, index) => {
				newOptions.push({
					label: item[labelProp],
					value: item[valueProp]
				});
			});
		});

		return newOptions;
	}

	validateFields (keys, getValue = (key) => {}) {
		let allFieldsAreValid = true;

		if (_.isEmpty(keys)) {

			return false;
		}

		_.each(keys, (key) => {
			const isValid = this.validate(getValue(key), key);

			if (isValid !== true) {

				allFieldsAreValid = false;
			}
		});

		return allFieldsAreValid;
	}

	validate (value, key) {
		const fieldConfig  = _.get(formConfig, key);

		if (fieldConfig.required && hasValue(value) !== true) {

			return false;
		}

		let isValid = true;

		const { validators = [] } = fieldConfig;

		// Allows for validator functions whose name matches the field key
		validators.push(key.replace(/^[^\.]+\./, ''));

		_.each(validators, (validator) => {
			const validatorFunc = validatorsFunctions[validator];

			if (_.isFunction(validatorFunc) && validatorFunc(value, key, this, fieldConfig) !== true) {

				isValid = false;

				// Stop iterating if invalid
				return isValid;
			}
		});

		const validationValue = {};
		validationValue[key] = isValid;
		this.validation = validationValue;

		return isValid;
	}
}

const formStore = new FormStore();
export default formStore;
